import CmsButton from "@/components/CmsButton";
import { Search, FileText, Rocket, HeadphonesIcon, Shield, Cloud, Server, Database, Wifi, Monitor, Cpu, Lock, Globe, Code, Smartphone, HardDrive, Network, Settings, HelpCircle } from "lucide-react";
import { useEffect, useRef } from "react";
import type { LucideIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useContentMulti } from "@/hooks/useContent";

const marqueeIcons: LucideIcon[] = [
  Shield, Cloud, Server, Database, Wifi, Monitor, Cpu, Lock, Globe, Code, Smartphone, HardDrive, Network, Settings,
];

const stepIcons = [Search, FileText, Rocket, HeadphonesIcon];

const HowItWorksHeader = () => {
  const c = useContentMulti({
    "how.label": "Our Process",
    "how.heading": "How It Works",
    "how.subheading": "A streamlined four-step process that takes you from assessment to ongoing support.",
  });
  return (
    <div className="text-center max-w-2xl mx-auto mb-16">
      <p className="text-primary-accessible font-display text-sm uppercase tracking-[0.2em] mb-3">{c["how.label"]}</p>
      <h2 className="font-display text-3xl md:text-5xl font-bold mb-6">
        {c["how.heading"].includes("Works") ? <>How It <span className="text-primary">Works</span></> : c["how.heading"]}
      </h2>
      <p className="text-muted-foreground">{c["how.subheading"]}</p>
    </div>
  );
};

const HowItWorksSteps = () => {
  const c = useContentMulti({
    "how.step1_title": "Assess", "how.step1_desc": "We analyze your current infrastructure and identify opportunities for improvement.",
    "how.step2_title": "Plan", "how.step2_desc": "Our team creates a comprehensive strategy tailored to your business goals.",
    "how.step3_title": "Implement", "how.step3_desc": "We deploy solutions with minimal disruption to your day-to-day operations.",
    "how.step4_title": "Support", "how.step4_desc": "Ongoing monitoring and 24/7 support to ensure everything runs smoothly.",
  });

  const steps = [1, 2, 3, 4].map((n, i) => ({
    icon: stepIcons[i],
    num: `0${n}`,
    title: c[`how.step${n}_title`],
    desc: c[`how.step${n}_desc`],
  }));

  return (
    <div className="relative">
      <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-px bg-border -translate-y-1/2" />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
        {steps.map((s) => (
          <div key={s.num} className="relative text-center group">
            <div className="relative z-10 w-20 h-20 rounded-full border-2 border-border bg-background mx-auto mb-6 flex items-center justify-center group-hover:border-primary transition-colors duration-300">
              <s.icon className="h-8 w-8 text-primary" />
            </div>
            <span className="text-xs text-primary font-display tracking-widest">{s.num}</span>
            <h3 className="font-display text-xl font-semibold mt-1 mb-3">{s.title}</h3>
            <p className="text-sm text-muted-foreground max-w-xs mx-auto">{s.desc}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

const HowItWorks = () => {
  const cta = useContentMulti({ "how.cta_button": "Got questions?", "how.cta_button_link": "#faq" });
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const sectionRef = useRef<HTMLDivElement>(null);
  const isVisibleRef = useRef(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let animId: number;
    const dpr = window.devicePixelRatio || 1;

    const resize = () => {
      const rect = canvas.parentElement!.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      ctx.scale(dpr, dpr);
      canvas.style.width = rect.width + "px";
      canvas.style.height = rect.height + "px";
    };
    resize();
    window.addEventListener("resize", resize);

    interface Gear { x: number; y: number; r: number; teeth: number; speed: number; angle: number; opacity: number; drift: number; }
    interface Node { progress: number; speed: number; size: number; opacity: number; }

    const w = () => canvas.width / dpr;
    const h = () => canvas.height / dpr;

    const gears: Gear[] = Array.from({ length: 8 }, () => ({
      x: Math.random() * 1200, y: Math.random() * 600, r: 20 + Math.random() * 35,
      teeth: Math.floor(6 + Math.random() * 6), speed: (Math.random() - 0.5) * 0.008,
      angle: Math.random() * Math.PI * 2, opacity: 0.04 + Math.random() * 0.06, drift: 0.1 + Math.random() * 0.15,
    }));

    const nodes: Node[] = Array.from({ length: 18 }, () => ({
      progress: Math.random(), speed: 0.0008 + Math.random() * 0.0015,
      size: 1.5 + Math.random() * 2.5, opacity: 0.15 + Math.random() * 0.25,
    }));

    const getPathPoint = (t: number, idx: number) => {
      const cw = w(); const ch = h();
      const row = idx % 3; const yBase = ch * 0.2 + row * (ch * 0.3);
      return { x: t * cw, y: yBase + Math.sin(t * Math.PI * 3 + idx) * 40 };
    };

    const drawGear = (g: Gear) => {
      ctx.save(); ctx.globalAlpha = g.opacity; ctx.translate(g.x, g.y); ctx.rotate(g.angle);
      ctx.strokeStyle = "hsl(0, 72%, 51%)"; ctx.lineWidth = 1;
      ctx.beginPath(); ctx.arc(0, 0, g.r * 0.5, 0, Math.PI * 2); ctx.stroke();
      for (let i = 0; i < g.teeth; i++) {
        const a = (i / g.teeth) * Math.PI * 2;
        ctx.beginPath();
        ctx.moveTo(Math.cos(a - 0.15) * g.r * 0.7, Math.sin(a - 0.15) * g.r * 0.7);
        ctx.lineTo(Math.cos(a - 0.1) * g.r, Math.sin(a - 0.1) * g.r);
        ctx.lineTo(Math.cos(a + 0.1) * g.r, Math.sin(a + 0.1) * g.r);
        ctx.lineTo(Math.cos(a + 0.15) * g.r * 0.7, Math.sin(a + 0.15) * g.r * 0.7);
        ctx.stroke();
      }
      ctx.beginPath(); ctx.arc(0, 0, g.r * 0.7, 0, Math.PI * 2); ctx.stroke();
      ctx.restore();
    };

    const animate = () => {
      if (!isVisibleRef.current || document.documentElement.classList.contains("chat-open")) { animId = requestAnimationFrame(animate); return; }
      const cw = w(); const ch = h();
      ctx.clearRect(0, 0, cw, ch);
      for (let i = 0; i < 5; i++) {
        ctx.beginPath(); ctx.strokeStyle = `hsla(0, 72%, 51%, 0.04)`; ctx.lineWidth = 1;
        for (let t = 0; t <= 1; t += 0.005) { const p = getPathPoint(t, i); t === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y); }
        ctx.stroke();
      }
      for (const g of gears) { g.angle += g.speed; g.y += Math.sin(Date.now() * 0.001 + g.x) * 0.05; drawGear(g); }
      for (const n of nodes) {
        n.progress += n.speed; if (n.progress > 1) n.progress = 0;
        const pathIdx = Math.floor(Math.random() * 1000) % 5;
        const p = getPathPoint(n.progress, pathIdx);
        const grad = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, n.size * 4);
        grad.addColorStop(0, `hsla(0, 72%, 51%, ${n.opacity * 0.5})`); grad.addColorStop(1, `hsla(0, 72%, 51%, 0)`);
        ctx.fillStyle = grad; ctx.beginPath(); ctx.arc(p.x, p.y, n.size * 4, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = `hsla(0, 72%, 51%, ${n.opacity})`; ctx.beginPath(); ctx.arc(p.x, p.y, n.size, 0, Math.PI * 2); ctx.fill();
      }
      animId = requestAnimationFrame(animate);
    };
    animate();

    const section = sectionRef.current;
    let observer: IntersectionObserver | undefined;
    if (section) {
      observer = new IntersectionObserver(([entry]) => { isVisibleRef.current = entry.isIntersecting; }, { threshold: 0 });
      observer.observe(section);
    }
    return () => { cancelAnimationFrame(animId); window.removeEventListener("resize", resize); observer?.disconnect(); };
  }, []);

  return (
    <section id="process" ref={sectionRef} className="py-24 relative overflow-hidden">
      <canvas ref={canvasRef} className="absolute inset-0 pointer-events-none" aria-hidden="true" />
      <div className="container mx-auto px-4 relative z-10">
        <HowItWorksHeader />
        <HowItWorksSteps />

        <div className="mt-16 overflow-hidden pointer-events-none">
          <div className="icon-marquee flex whitespace-nowrap">
            {[...marqueeIcons, ...marqueeIcons].map((Icon, i) => (
              <Icon key={i} className="text-primary mx-8 shrink-0" size={36} />
            ))}
          </div>
        </div>
        <div className="mt-8 text-center pointer-events-auto">
          <CmsButton
            size="lg"
            className="bg-green-600 hover:bg-green-700 text-white border-green-600"
            href={cta["how.cta_button_link"]}
          >
            <HelpCircle className="mr-2 h-5 w-5" />
            {cta["how.cta_button"]}
          </CmsButton>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
